import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { useClientes } from '@/modules/users/hooks/useGroups'
import type { CampaniaDetail, CampaniaCreatePayload } from '../types'

const campaniaSchema = z.object({
  titulo: z.string().min(1, 'El título es requerido'),
  descripcion: z.string().optional(),
  imagen_url: z.string().optional(),
  activa: z.boolean(),
  url_redireccion: z.string().optional(),
  permitir_multiples_respuestas: z.boolean(),
  usar_formulario_personalizado: z.boolean(),
  mensaje_bienvenida: z.string().optional(),
  tiempo_estimado: z.coerce.number().min(0).optional(),
  fecha_inicio: z.string().optional(),
  fecha_fin: z.string().optional(),
  grupo_laboratorio_id: z.coerce.number().nullable().optional(),
})

type CampaniaFormData = z.infer<typeof campaniaSchema>

interface CampaniaFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CampaniaCreatePayload) => void
  isSubmitting: boolean
  campania?: CampaniaDetail | null
  mode: 'create' | 'edit'
}

export function CampaniaFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  campania,
  mode,
}: CampaniaFormDialogProps) {
  const { data: clientes } = useClientes()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CampaniaFormData>({
    resolver: zodResolver(campaniaSchema),
    defaultValues: {
      titulo: '',
      descripcion: '',
      imagen_url: '',
      activa: true,
      url_redireccion: '',
      permitir_multiples_respuestas: false,
      usar_formulario_personalizado: false,
      mensaje_bienvenida: '',
      tiempo_estimado: 5,
      fecha_inicio: '',
      fecha_fin: '',
      grupo_laboratorio_id: null,
    },
  })

  const usarPersonalizado = watch('usar_formulario_personalizado')

  useEffect(() => {
    if (open && mode === 'edit' && campania) {
      reset({
        titulo: campania.titulo,
        descripcion: campania.descripcion || '',
        imagen_url: campania.imagen_url || '',
        activa: campania.activa,
        url_redireccion: campania.url_redireccion || '',
        permitir_multiples_respuestas: campania.permitir_multiples_respuestas,
        usar_formulario_personalizado: campania.usar_formulario_personalizado,
        mensaje_bienvenida: campania.mensaje_bienvenida || '',
        tiempo_estimado: campania.tiempo_estimado || 5,
        fecha_inicio: campania.fecha_inicio?.slice(0, 16) || '',
        fecha_fin: campania.fecha_fin?.slice(0, 16) || '',
        grupo_laboratorio_id: campania.grupo_laboratorio_id ?? null,
      })
    } else if (open && mode === 'create') {
      reset({
        titulo: '',
        descripcion: '',
        imagen_url: '',
        activa: true,
        url_redireccion: '',
        permitir_multiples_respuestas: false,
        usar_formulario_personalizado: false,
        mensaje_bienvenida: '',
        tiempo_estimado: 5,
        fecha_inicio: '',
        fecha_fin: '',
        grupo_laboratorio_id: null,
      })
    }
  }, [open, mode, campania, reset])

  const handleFormSubmit = (data: CampaniaFormData) => {
    const payload: CampaniaCreatePayload = {
      ...data,
      fecha_inicio: data.fecha_inicio || null,
      fecha_fin: data.fecha_fin || null,
      imagen_url: data.imagen_url || null,
      grupo_laboratorio_id: data.grupo_laboratorio_id || null,
    }
    onSubmit(payload)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Nueva Campaña' : 'Editar Campaña'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">Título *</Label>
            <Input id="titulo" {...register('titulo')} placeholder="Nombre de la campaña" />
            {errors.titulo && <p className="text-sm text-destructive">{errors.titulo.message}</p>}
          </div>

          <div className="space-y-2">
            <Label>Laboratorio (cliente)</Label>
            <Select
              value={watch('grupo_laboratorio_id')?.toString() || 'none'}
              onValueChange={(v) => setValue('grupo_laboratorio_id', v === 'none' ? null : Number(v))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar laboratorio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {clientes?.map((c) => (
                  <SelectItem key={c.grupo_id} value={c.grupo_id.toString()}>
                    {c.grupo_nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea id="descripcion" {...register('descripcion')} placeholder="Descripción de la campaña" rows={3} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="imagen_url">URL de imagen</Label>
            <Input id="imagen_url" {...register('imagen_url')} placeholder="https://..." />
          </div>

          <div className="space-y-2">
            <Label htmlFor="url_redireccion">URL de redirección</Label>
            <Input id="url_redireccion" {...register('url_redireccion')} placeholder="https://..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_inicio">Fecha inicio</Label>
              <Input id="fecha_inicio" type="datetime-local" {...register('fecha_inicio')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_fin">Fecha fin</Label>
              <Input id="fecha_fin" type="datetime-local" {...register('fecha_fin')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tiempo_estimado">Tiempo estimado (minutos)</Label>
            <Input id="tiempo_estimado" type="number" {...register('tiempo_estimado')} min={0} />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="activa">Activa</Label>
            <Switch
              id="activa"
              checked={watch('activa')}
              onCheckedChange={(v) => setValue('activa', v)}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="permitir_multiples">Permitir múltiples respuestas</Label>
            <Switch
              id="permitir_multiples"
              checked={watch('permitir_multiples_respuestas')}
              onCheckedChange={(v) => setValue('permitir_multiples_respuestas', v)}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="usar_personalizado">Formulario personalizado</Label>
            <Switch
              id="usar_personalizado"
              checked={usarPersonalizado}
              onCheckedChange={(v) => setValue('usar_formulario_personalizado', v)}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>

          {usarPersonalizado && (
            <div className="space-y-2">
              <Label htmlFor="mensaje_bienvenida">Mensaje de bienvenida</Label>
              <Textarea
                id="mensaje_bienvenida"
                {...register('mensaje_bienvenida')}
                placeholder="Mensaje que se mostrará al inicio del formulario"
                rows={3}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-teal-800 hover:bg-teal-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Crear' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
